import React from 'react';
import { useLocation } from 'react-router';
import { useNavigateWithBreadcrumbs } from '../../../hooks';
import { Card, VStack } from '@adc/parallax-component-library';
import DynamicForm, {
  DynamicFormConfig,
  DynamicFormFieldType,
} from '../../form/DynamicForm';
import { Subject, UpdateRequest } from '../../../models';
import { EntityStatus, subjectIdSchema } from 'adc-sirius-module-common';
import * as Yup from 'yup';
import i18n from '../../../utilities/i18n';
import { enumToChoiceOptions } from '../../../utilities/helpers';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  subjectQueryOptions,
  updateSubjectMutateOptions,
} from '../../../queryOptions';

type SubjectHeaderProps = {
  subject: Subject;
  children?: React.JSX.Element;
};

type SubjectFormValues = Omit<Subject, 'status'> &
  UpdateRequest & {
    status: string; // converted to string for dropdown input
  };

/**
 * SubjectHeader component displays the header information for a specific subject.
 * @param {Subject} subject - The subject object containing details to display.
 * @param {React.JSX.Element} children - The child components to render within the header.
 * @returns {React.JSX.Element}
 */
export default function SubjectHeader({
  subject,
  children,
}: SubjectHeaderProps) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigateWithBreadcrumbs = useNavigateWithBreadcrumbs();

  const updateBreadcrumb = (label) => {
    navigateWithBreadcrumbs(location.pathname, label, label, {
      replace: true,
    });
  };

  const mapFormValuesToSubject = (
    values: Partial<SubjectFormValues>,
  ): Subject => {
    const { newUpdateReason, ...updatedValues } = values;
    const updatedSubject = {
      ...subject,
      ...updatedValues,
      status: Number(updatedValues.status ?? subject.status), // convert status back to number for API
      updateReason: newUpdateReason as string,
    };

    return updatedSubject;
  };

  const mapSubjectToFormValues = (subject: Subject): SubjectFormValues => ({
    ...subject,
    status: String(subject.status),
    newUpdateReason: '',
  });

  const formConfig: DynamicFormConfig<SubjectFormValues> = {
    fields: [
      {
        name: 'name',
        label: i18n.t('Page_Subject.Form.name'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'description',
        label: i18n.t('Page_Subject.Form.description'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'status',
        label: i18n.t('Page_Subject.Form.status'),
        type: DynamicFormFieldType.CHOICE,
        isEditable: true,
        choiceOptions: enumToChoiceOptions(EntityStatus, 'Status', {
          sortBy: 'name',
        }),
      },
      {
        name: 'region',
        label: i18n.t('Page_Subject.Form.region'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'type',
        label: i18n.t('Page_Subject.Form.type'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'newUpdateReason',
        label: i18n.t('Page_Subject.Form.newUpdateReason'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
        isEditOnly: true,
      },
      {
        name: 'createdBy',
        label: i18n.t('Page_Subject.Form.createdBy'),
        type: DynamicFormFieldType.TEXT,
      },
      {
        name: 'createdOn',
        label: i18n.t('Page_Subject.Form.createdOn'),
        type: DynamicFormFieldType.DATE,
      },
      {
        name: 'updatedBy',
        label: i18n.t('Page_Subject.Form.updatedBy'),
        type: DynamicFormFieldType.TEXT,
      },
      {
        name: 'updatedOn',
        label: i18n.t('Page_Subject.Form.updatedOn'),
        type: DynamicFormFieldType.DATE,
      },
      {
        name: 'updateReason',
        label: i18n.t('Page_Subject.Form.updateReason'),
        type: DynamicFormFieldType.TEXT,
      },
    ],
    validationSchema: {
      key: Yup.string().required(i18n.t('Form.genericRequired')),
      name: Yup.string()
        .required(i18n.t('Form.genericRequired'))
        .matches(
          subjectIdSchema.matcher,
          i18n.t('Form.allowedSpecialCharacters', {
            characters: subjectIdSchema.allowedSpecialCharacters.join(' '),
          }),
        )
        .test(
          'len',
          () =>
            i18n.t('Form.genericMaxLength', {
              count: subjectIdSchema.maxLength,
            }),
          (val) =>
            subjectIdSchema.maxLength && val
              ? val.length < subjectIdSchema.maxLength
              : true,
        ),
      description: Yup.string(),
      deviceId: Yup.string(),
      userNotes: Yup.array(),
      siteKey: Yup.string().required(i18n.t('Form.genericRequired')),
      status: Yup.string().required(i18n.t('Form.genericRequired')),
      createdBy: Yup.string().required(i18n.t('Form.genericRequired')),
      createdOn: Yup.string().required(i18n.t('Form.genericRequired')),
      updatedBy: Yup.string(),
      updatedOn: Yup.string(),
      updateReason: Yup.string(),
      systemNotes: Yup.array(),
      newUpdateReason: Yup.string().required(i18n.t('Form.genericRequired')),
      region: Yup.string(),
      type: Yup.string(),
      state: Yup.number(),
    },
    initialValues: mapSubjectToFormValues(subject),
  };

  const updateSubject = useMutation(updateSubjectMutateOptions());

  const handleSubmit = async (values: Partial<SubjectFormValues>) => {
    const subjectValues = mapFormValuesToSubject(values);
    const updatedSubject = await updateSubject.mutateAsync({
      subjectKey: subject.key,
      updatedSubjectValues: subjectValues,
    });

    const subjectQueryKey = subjectQueryOptions({
      key: updatedSubject.key,
    }).queryKey;

    queryClient.setQueryData(subjectQueryKey, updatedSubject);

    updateBreadcrumb(updatedSubject.name);

    const updatedFormValues = mapSubjectToFormValues(updatedSubject);

    return updatedFormValues;
  };

  return (
    <VStack flexGrow={1} gap={'$4'} testID="subject-details">
      <Card>
        <DynamicForm
          id="subject-details"
          title={i18n.t('Page_Subject.title')}
          formConfig={formConfig}
          onSave={handleSubmit}
          maxRows={Math.ceil(formConfig.fields.length / 2)}
        >
          {children}
        </DynamicForm>
      </Card>
    </VStack>
  );
}
